import * as React from "react";

import { type CrudFilter, useList } from "@refinedev/core";

import { facetCounts, selectAreaRows } from "@/pages/areas/lib/area-hits";
import { citiesFromFacet } from "@/pages/areas/lib/cities";
import type { AreaListItem } from "@/types/area";

/** Typesense's per-page ceiling. */
export const EXPLORE_MAX = 250;

export interface AreaExploreArgs {
  filters: CrudFilter[];
  pageSize?: number;
  /** Facet fields to count over the whole filtered set. */
  facetBy?: string[];
  enabled?: boolean;
}

export interface AreaExplore {
  rows: AreaListItem[];
  /** Typesense's `found`: every match, not just the rows returned. */
  total: number;
  /** The raw `facet_counts` of the response; read a field with `facetCounts`. */
  facets: unknown;
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * One `GET /areas/explore` query for the option lists, through the same
 * provider as the table. The search box lives in the URL and Refine folds it into every
 * query's meta, so `query` is pinned to `*` here: a search must not shrink a city list.
 */
export const useAreaExplore = ({
  filters,
  pageSize = EXPLORE_MAX,
  facetBy,
  enabled = true,
}: AreaExploreArgs): AreaExplore => {
  const { result, query } = useList({
    resource: "areas",
    dataProviderName: "areas",
    pagination: { currentPage: 1, pageSize },
    filters,
    meta: {
      query: "*",
      queryBy: "name,aliases",
      ...(facetBy ? { facetBy } : {}),
    },
    queryOptions: { enabled, select: selectAreaRows },
  });
  const facets = (result as { facets?: unknown } | undefined)?.facets;
  return {
    rows: (result?.data ?? []) as unknown as AreaListItem[],
    total: result?.total ?? 0,
    facets,
    isLoading: enabled && query.isLoading,
    error: query.error ?? undefined,
  };
};

/** Every city that has an area (or, with `filters`, a matching one), from the `cityKey` facet. */
export const useAreaCities = (filters: CrudFilter[] = []) => {
  const explore = useAreaExplore({
    filters,
    pageSize: 1,
    facetBy: ["cityKey"],
  });
  const { facets } = explore;
  return {
    cities: React.useMemo(
      () => citiesFromFacet(facetCounts(facets, "cityKey")),
      [facets],
    ),
    isLoading: explore.isLoading,
    error: explore.error,
  };
};
